import type {
  OperatorAffiliateDetail,
  AffiliatePlan,
} from "../../../../types/affiliates";
import type {
  AffiliatePaymentCpa,
  AffiliatePaymentPlan,
  AffiliatePaymentRevShare,
  AffiliatePersonalInfo,
  AffiliateAddress as MockAffiliateAddress,
} from "./affiliatePersonalInfoData";

const cpaFromPlan = (plan: AffiliatePlan): AffiliatePaymentCpa | undefined => {
  if (plan.cpa_payment_per_action == null) return undefined;
  return {
    type: "cpa",
    amountUsd: plan.cpa_payment_per_action,
    actionType: plan.cpa_action_type ?? null,
  };
};

const revShareFromPlan = (
  plan: AffiliatePlan,
): AffiliatePaymentRevShare | undefined => {
  const sportsbookPct = plan.revenue_share_pct_sportsbook;
  const casinoPct = plan.revenue_share_pct_casino;
  const scope =
    plan.revenue_source === "sportsbook"
      ? "sportsbook"
      : plan.revenue_source === "sportsbook_casino"
        ? "sportsbook_and_casino"
        : "casino";
  const minimums = {
    minimumSportsbookProfitUsd:
      plan.min_sportsbook_profit_threshold ?? undefined,
    minimumCasinoProfitUsd: plan.min_casino_profit_threshold ?? undefined,
  };

  // Both products → split shape so SB and Casino can show different rates.
  if (scope === "sportsbook_and_casino" && (sportsbookPct != null || casinoPct != null)) {
    return {
      type: "rev_share",
      scope,
      sportsbookPercentage: sportsbookPct ?? undefined,
      casinoPercentage: casinoPct ?? undefined,
      ...minimums,
    };
  }

  // Prefer the side that matches the source; fall back so a null/unset
  // source doesn't blank the card.
  const sourcePct = scope === "sportsbook" ? sportsbookPct : casinoPct;
  const revSharePct = sourcePct ?? casinoPct ?? sportsbookPct;
  if (revSharePct == null) return undefined;
  return {
    type: "rev_share",
    scope,
    percentage: revSharePct,
    ...minimums,
  };
};

const paymentFromPlan = (
  plan: AffiliatePlan | null,
): AffiliatePaymentPlan | undefined => {
  if (!plan) return undefined;
  if (plan.plan_type === "CPA") return cpaFromPlan(plan);
  if (plan.plan_type === "REVENUE_SHARE") return revShareFromPlan(plan);
  if (plan.plan_type === "HYBRID") {
    // Hybrid is a flat plan carrying BOTH the CPA and rev-share fields —
    // rebuild each leaf and render whichever ones the plan populated.
    const components = [cpaFromPlan(plan), revShareFromPlan(plan)].filter(
      (component): component is AffiliatePaymentCpa | AffiliatePaymentRevShare =>
        component != null,
    );
    return components.length ? { type: "hybrid", components } : undefined;
  }
  return undefined;
};

const addressFromDetail = (
  detail: OperatorAffiliateDetail,
): MockAffiliateAddress => ({
  line1: detail.address?.line1 ?? "",
  line2: detail.address?.line2 ?? undefined,
  city: detail.address?.city ?? "",
  region: detail.address?.state ?? "",
  postalCode: detail.address?.zip ?? "",
  country: detail.address?.country ?? "",
});

export const personalInfoFromDetail = (
  detail: OperatorAffiliateDetail,
): AffiliatePersonalInfo => ({
  affiliateId: detail.affiliate_id,
  fullName: detail.contact_name || detail.company_name || "",
  email: detail.email ?? "",
  joinedAt: "",
  userId: detail.cognito_sub ?? "",
  websiteUrl: detail.website_url ?? "",
  address: addressFromDetail(detail),
  payment: paymentFromPlan(detail.current_plan),
  contract: {
    signedAt: "",
    renewalAt: "",
    fileName: detail.contract_s3_key ?? "",
    fileUrl: detail.contract_s3_key ?? "",
  },
});
